import axios from 'axios';

class LogCollectionService {
    constructor() {
        this.logs = [];
        this.maxLogsStored = 500;
        this.isCollecting = false;
        this.collectionStartTime = null;
        this.networkMonitoringEnabled = false;
        this.startedBySeekDebug = false;
    }

    startCollection(options = { captureNetwork: false }) {
        if (this.isCollecting) return;

        this.isCollecting = true;
        this.collectionStartTime = new Date();
        this.logs = [];
        this.networkMonitoringEnabled = !!options.captureNetwork;

        // Start listening to console events
        this.originalConsoleLog = console.log;
        this.originalConsoleError = console.error;
        this.originalConsoleWarn = console.warn;
        this.originalConsoleInfo = console.info;

        // Override console methods
        console.log = (...args) => {
            this.captureLog('log', args);
            this.originalConsoleLog.apply(console, args);
        };

        console.error = (...args) => {
            this.captureLog('error', args);
            this.originalConsoleError.apply(console, args);
        };

        console.warn = (...args) => {
            this.captureLog('warn', args);
            this.originalConsoleWarn.apply(console, args);
        };

        console.info = (...args) => {
            this.captureLog('info', args);
            this.originalConsoleInfo.apply(console, args);
        };

        // Add global error handler
        window.addEventListener('error', this.handleError);
        window.addEventListener('unhandledrejection', this.handleRejection);

        // Add network request monitoring only if specifically enabled
        if (this.networkMonitoringEnabled) {
            this.setupNetworkMonitoring();
        } else {
            // Just log that we're skipping network monitoring
            this.captureLog('log', ['Network monitoring disabled to avoid conflicts']);
        }

        this.captureLog('log', ['Log collection started']);
        this.logTaggedEvent('log_collection_started', {
            captureNetwork: this.networkMonitoringEnabled,
            status: this.getCollectionStatus(),
        }, ['diagnostics', 'startup']);
        return true;
    }

    stopCollection() {
        if (!this.isCollecting) return;

        this.captureLog('log', ['Log collection stopped']);

        // Restore original console methods
        console.log = this.originalConsoleLog;
        console.error = this.originalConsoleError;
        console.warn = this.originalConsoleWarn;
        console.info = this.originalConsoleInfo;

        // Remove event listeners
        window.removeEventListener('error', this.handleError);
        window.removeEventListener('unhandledrejection', this.handleRejection);

        // Stop network monitoring if it was enabled
        if (this.networkMonitoringEnabled) {
            this.teardownNetworkMonitoring();
        }

        this.isCollecting = false;
        this.networkMonitoringEnabled = false;
        this.startedBySeekDebug = false;

        return this.logs;
    }

    getUserContext() {
        try {
            const authUser = JSON.parse(localStorage.getItem('authUser') || 'null');
            const videoProps = JSON.parse(localStorage.getItem('videoProps') || 'null');
            const selectedLng = JSON.parse(localStorage.getItem('setSelectedLng') || 'null');
            return {
                email: authUser?.email || null,
                userType: authUser?.userType || null,
                videoId: videoProps?.id || null,
                videoName: videoProps?.videoName || null,
                selectedLanguage: selectedLng || null,
            };
        } catch (_error) {
            return {
                email: null,
                userType: null,
                videoId: null,
                videoName: null,
                selectedLanguage: null,
            };
        }
    }

    getNetworkQualitySnapshot() {
        const connection =
            navigator.connection || navigator.mozConnection || navigator.webkitConnection;
        return {
            online: navigator.onLine,
            userAgent: navigator.userAgent,
            effectiveType: connection?.effectiveType || null,
            downlink: connection?.downlink || null,
            rtt: connection?.rtt || null,
            saveData: connection?.saveData || false,
        };
    }

    logTaggedEvent(eventName, payload = {}, tags = []) {
        if (!this.isCollecting) return;

        const structuredPayload = {
            eventName,
            tags,
            payload,
            userContext: this.getUserContext(),
            network: this.getNetworkQualitySnapshot(),
        };

        this.captureLog('debug-event', [structuredPayload]);
    }

    setSeekDebugMode(enabled, options = { captureNetwork: true }) {
        if (enabled) {
            if (!this.isCollecting) {
                this.startedBySeekDebug = true;
                this.startCollection({ captureNetwork: !!options.captureNetwork });
            }
            this.logTaggedEvent(
                'seek_debug_enabled',
                {
                    captureNetwork: !!options.captureNetwork,
                },
                ['seek-debug', 'diagnostics'],
            );
            return;
        }

        this.logTaggedEvent('seek_debug_disabled', {}, ['seek-debug', 'diagnostics']);

        if (this.startedBySeekDebug && this.isCollecting) {
            this.stopCollection();
        }
    }

    captureLog(level, args) {
        if (!this.isCollecting || !args.length) return;

        // Attempt to safely stringify objects
        const safeArgs = args.map((arg) => {
            if (arg === null) return 'null';
            if (arg === undefined) return 'undefined';
            if (typeof arg === 'string') return arg;
            if (arg instanceof Error) return `${arg.name}: ${arg.message}\n${arg.stack || ''}`;
            try {
                return JSON.stringify(arg);
            } catch (e) {
                return String(arg);
            }
        });

        this.logs.push({
            timestamp: new Date().toISOString(),
            level,
            message: safeArgs.join(' '),
        });

        // Trim logs if they exceed the maximum
        if (this.logs.length > this.maxLogsStored) {
            this.logs = this.logs.slice(-this.maxLogsStored);
        }
    }

    handleError = (event) => {
        const message = event?.message || '';

        // Ignore known benign browser ResizeObserver loop warnings.
        if (
            message.includes('ResizeObserver loop completed with undelivered notifications') ||
            message.includes('ResizeObserver loop limit exceeded')
        ) {
            if (typeof event?.preventDefault === 'function') {
                event.preventDefault();
            }
            return;
        }

        this.captureLog('error', [
            `Runtime Error: ${event.message} at ${event.filename}:${event.lineno}:${event.colno}`,
            event.error,
        ]);
    };

    handleRejection = (event) => {
        this.captureLog('error', ['Unhandled Promise Rejection:', event.reason]);
    };

    // Alternative approach using a safer monitoring strategy
    setupNetworkMonitoring() {
        // We'll use a different approach that doesn't override fetch directly
        // This avoids issues with other libraries that might be using fetch

        // Monitor XMLHttpRequest safely
        const self = this;

        // Use a more targeted approach with XMLHttpRequest
        this.xhrOpenProxy = function (originalFn) {
            return function (method, url, ...rest) {
                this._requestMethod = method;
                this._requestUrl = url;
                this._requestId = Math.random().toString(36).substring(2, 10);
                self.captureLog('network', [`XHR Request ${this._requestId}: ${method} ${url}`]);
                return originalFn.apply(this, [method, url, ...rest]);
            };
        };

        this.xhrSendProxy = function (originalFn) {
            return function (body) {
                const xhr = this;
                const startTime = Date.now();
                const requestId = xhr._requestId;

                function addListeners() {
                    xhr.addEventListener('load', function () {
                        const duration = Date.now() - startTime;
                        self.captureLog('network', [
                            `XHR Response ${requestId}: ${xhr.status} (${duration}ms) - ${xhr._requestUrl}`,
                        ]);
                    });

                    xhr.addEventListener('error', function () {
                        const duration = Date.now() - startTime;
                        self.captureLog('network', [
                            `XHR Error ${requestId}: Failed (${duration}ms) - ${xhr._requestUrl}`,
                        ]);
                    });
                }

                addListeners();
                return originalFn.apply(this, [body]);
            };
        };

        // Store original methods
        this.originalXHROpen = XMLHttpRequest.prototype.open;
        this.originalXHRSend = XMLHttpRequest.prototype.send;

        // Apply proxies
        XMLHttpRequest.prototype.open = this.xhrOpenProxy(this.originalXHROpen);
        XMLHttpRequest.prototype.send = this.xhrSendProxy(this.originalXHRSend);

        // Log successful setup
        this.captureLog('log', ['XHR monitoring setup successfully']);
    }

    teardownNetworkMonitoring() {
        // Restore original methods
        if (this.originalXHROpen) {
            XMLHttpRequest.prototype.open = this.originalXHROpen;
        }

        if (this.originalXHRSend) {
            XMLHttpRequest.prototype.send = this.originalXHRSend;
        }

        this.captureLog('log', ['Network monitoring stopped']);
    }

    // Create a simple method to manually log network activity from components
    logNetworkActivity(status, url, duration, details = {}) {
        if (!this.isCollecting) return;

        const message = `Fetch ${status}: ${url} (${duration}ms)`;
        this.captureLog('network', [message, JSON.stringify(details)]);
    }

    async submitBug(bugReport) {
        try {
            // Include logs with the report
            const reportWithLogs = {
                title: bugReport.title,
                description: bugReport.description,
                stepsToReproduce: bugReport.steps,
                browser: navigator.userAgent,
                timestamp: new Date().toISOString(),
                collectionStartTime: this.collectionStartTime?.toISOString(),
                systemInfo: bugReport.systemInfo || null,
                editorVersion: (bugReport.contextData && bugReport.contextData.editorVersion) || bugReport.editorVersion || null,
                logsJson: bugReport.includeLogs ? JSON.stringify(this.logs) : null,
            };

            // Still save locally as backup
            try {
                const savedReports = JSON.parse(localStorage.getItem('bugReports') || '[]');
                savedReports.push(reportWithLogs);
                localStorage.setItem('bugReports', JSON.stringify(savedReports));
            } catch (localError) {
                this.captureLog('warn', ['Failed to save bug report locally:', localError]);
            }

            // Send to server
            this.captureLog('log', [`Sending bug report to server: ${reportWithLogs.title}`]);

            const authUser = JSON.parse(localStorage.getItem('authUser'));
            const authToken = JSON.parse(localStorage.getItem('authToken'));
            const endpoint = process.env.REACT_APP_API_URL + '/api/v1/bug-reports';
            // Use axios to send the report to the server
            const response = await axios
                .post(
                    endpoint,
                    {
                        title: bugReport.title,
                        description: bugReport.description,
                        systemInfo: bugReport.systemInfo || null,
                        editorVersion: (bugReport.contextData && bugReport.contextData.editorVersion) || bugReport.editorVersion || null,
                        stepsToReproduce: bugReport.steps,
                        browser: navigator.userAgent,
                        timestamp: new Date().toISOString(),
                        collectionStartTime: this.collectionStartTime?.toISOString(),
                        logsJson: bugReport.includeLogs ? JSON.stringify(this.logs) : null,
                    },
                    {
                        headers: {
                            email: authUser.email,
                            auth: authToken,
                            'Access-Control-Allow-Origin': '*',
                            'Access-Control-Allow-Credentials': true,
                        },
                    },
                )
                .then((response) => {
                    if (response.status >= 200 && response.status < 300) {
                        this.captureLog('log', [`Bug report saved on server: ${reportWithLogs.title}`]);
                        return {
                            success: true,
                            message: 'Bug report submitted successfully',
                            reportId: response.data.id || null,
                        };
                    } else {
                        throw new Error(`Server returned status code ${response.status}`);
                    }
                })
                .catch((error) => {
                    this.captureLog('error', ['Failed to submit bug report to server:', error]);
                    throw error;
                });
        } catch (error) {
            this.captureLog('error', ['Failed to submit bug report to server:', error]);

            // Return an object with error information
            return {
                success: false,
                message: `Error submitting bug report: ${error.message}`,
                error: error,
            };
        }
    }

    downloadLogs() {
        try {
            const data = JSON.stringify(this.logs, null, 2);
            const blob = new Blob([data], { type: 'application/json' });
            const url = URL.createObjectURL(blob);

            // Create download link
            const a = document.createElement('a');
            a.href = url;
            a.download = `amrutvaani-logs-${new Date().toISOString().split('T')[0]}.json`;
            document.body.appendChild(a);
            a.click();

            // Clean up
            setTimeout(() => {
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
            }, 0);
        } catch (error) {
            console.error('Failed to download logs:', error);
        }
    }

    getCollectionStatus() {
        return {
            isCollecting: this.isCollecting,
            logsCount: this.logs.length,
            startTime: this.collectionStartTime,
            networkMonitoring: this.networkMonitoringEnabled,
            startedBySeekDebug: this.startedBySeekDebug,
        };
    }

    getLogs() {
        return [...this.logs];
    }
}

// Create singleton instance
const logCollectionService = new LogCollectionService();
export default logCollectionService;
